// Component: SignalEvaluationPanel.jsx
// Purpose: Algo strategy monitoring. Per side: compact SUMMARY CARD + left-rail SIGNAL TREE.
//   Nodes branch off a left spine (git-log style). In-position cards include an SL↔TP
//   slider showing live price position between stop and target.
//   Driven live by WS nodeResults, orders map (for bracket trigger prices), and position state.

import { useCallback, useMemo } from "react";
//* Shared, case-insensitive direction classifier (mirrors backend exit_direction). Pure util (no
//  React/circular dep) — makes the live/paper Exit Monitor find an agent-authored data.type:"LONG"
//  SL/TP instead of dropping it via an exact-case compare (issue-strategy-graph-fe-drift, row 2c).
import {
  nodeDirection,
  collectTriggerTree,
  flattenTriggerTree,
  buildConditionDetail,
  resolveNameDetail,
  resolveDisplayName,
} from "../../Strategies/Modern/StrategyBuilder/utils/nodeUtils";

//* 50-17 — ONE shared condition tree (also the dashboard's), fed the tree collectTriggerTree walked.
import ConditionRailTree from "../../Strategies/Modern/StrategyBuilder/components/ConditionRailTree";
//* E5a — shared compact duration formatter (also used by AlgoDashboard's liveness note),
//  so a stale node's age reads "STALE 21h" instead of re-deriving duration math here.
import { formatAge } from "../utils/liveness";
//* R8.6-R8.10 — reuse verbatim (feedback_reuse_discovery_before_patching_loopbacks); never
//  reinvent a humanizer/badge for the same 3-way mode label OrdersTable already renders.
import { rejectReasonLabel, fullRejectReason } from "../../../utils/rejectReason";
import { ModeBadge } from "../../../components/trading/OrdersTable";
//* #10472 (R9) — bracket LEG ORDER ROWS are the primary SL/TP authority (same helper TradeCard's
//  bar and leg table use), read before liveLevels / the entry row's construction-time copy.
import { resolveCurrentBracketLevels, pickLevelPair } from "../../../utils/tradingTransforms";

/*>>> SIDE PALETTE <<<*/
const SIDE = {
  long: {
    label: "Long",
    rail: "bg-profit",
    ink: "text-profit",
    inkSoft: "text-profit",
    chipReady: "text-profit border-profit/40 bg-profit/[0.05]",
    chipLive: "text-profit border-profit/50 bg-profit/[0.08]",
    branch: "bg-profit/50",
    dot: "bg-profit",
    pillTint: "text-profit bg-profit/[0.12]",
    rowBg: "bg-profit/[0.08]",
    treeHeader: "text-profit",
    nodeMet: "border-profit/50 bg-profit/[0.06]",
    triggerReady: "bg-profit/[0.12] text-profit",
  },
  short: {
    label: "Short",
    rail: "bg-loss",
    ink: "text-loss",
    inkSoft: "text-loss",
    chipReady: "text-loss border-loss/40 bg-loss/[0.05]",
    chipLive: "text-loss border-loss/50 bg-loss/[0.08]",
    branch: "bg-loss/50",
    dot: "bg-loss",
    pillTint: "text-loss bg-loss/[0.12]",
    rowBg: "bg-loss/[0.08]",
    treeHeader: "text-loss",
    nodeMet: "border-loss/50 bg-loss/[0.06]",
    triggerReady: "bg-loss/[0.12] text-loss",
  },
};

const EXIT_ACCENT = {
  sl:        { ink: "text-loss",     dot: "bg-loss",     triggerReady: "bg-loss/[0.12] text-loss" },
  tp:        { ink: "text-profit",  dot: "bg-profit",  triggerReady: "bg-profit/[0.12] text-profit" },
  time_exit: { ink: "text-primary",      dot: "bg-primary",      triggerReady: "bg-primary/[0.12] text-primary" },
  exit_long: { ink: "text-fg-3",     dot: "bg-fg-3",     triggerReady: "bg-bg-3 text-fg-2" },
  exit_short:{ ink: "text-fg-3",     dot: "bg-fg-3",     triggerReady: "bg-bg-3 text-fg-2" },
};

const ENTRY_ACTION_FOR_SIDE = { long: "enter_long", short: "enter_short" };
const EXIT_ACTIONS = new Set(["sl", "tp", "time_exit", "exit_long", "exit_short"]);
//! signal ≡ position_trigger. The backend aliases them (graph_evaluator.py:623 "position_trigger is a
//  frontend alias for signal — both evaluate via _eval_signal_node()") and the FE tolerates BOTH
//  everywhere else (nodeVisualConfig TRIGGER_NODE_TYPES, TradeExecutionStep, nodeUtils, RDWatchDashboard…).
//  The FE toolbox emits position_trigger; agent/MCP-authored graphs may emit signal (e.g. c2f3bfb3's
//  sig_enter_long is type:"signal"). These two finders MUST accept BOTH — else an agent's signal-type
//  entry/exit vanishes from the live/paper Algo dashboard (no entry/exit details render).
const TRIGGER_NODE_TYPES = new Set(["signal", "position_trigger"]);

/*>>> GRAPH HELPERS <<<*/
function findEntryTrigger(nodes, side) {
  return nodes.find((n) => TRIGGER_NODE_TYPES.has(n.type) && n.data?.action === ENTRY_ACTION_FOR_SIDE[side]) || null;
}
function findExitTriggers(nodes, side) {
  return nodes.filter((n) => TRIGGER_NODE_TYPES.has(n.type) && EXIT_ACTIONS.has(n.data?.action) && nodeDirection(n) === side);
}
//--> Conditions feeding one trigger: ids of the prebuilt tree (nodeUtils collectTriggerTree, the ONE walk —
//*  stops at upstream triggers, includes time_window), mapped back to nodes in graph order.
function collectUpstream(strategyConfig, tree) {
  const nodes = strategyConfig?.nodes || [];
  const ids = new Set(flattenTriggerTree(tree));
  return nodes.filter((n) => ids.has(n.id));
}

function getIncomingEdges(strategyConfig, nodeId) {
  const edges = strategyConfig?.edges || [];
  return edges.filter((e) => e.target === nodeId);
}

function getTriggerParentIds(strategyConfig, nodeId) {
  const incoming = getIncomingEdges(strategyConfig, nodeId);
  const triggerInputParents = incoming
    .filter((e) => e.targetHandle === "trigger_input")
    .map((e) => e.source);
  if (triggerInputParents.length > 0) {
    return [...new Set(triggerInputParents)];
  }
  return [...new Set(incoming.map((e) => e.source))];
}

function getNodeTruth(res) {
  if (!res) return false;
  if (res.result === true) return true;
  if (res.triggered === true) return true;
  if (res.value === true) return true;
  if (typeof res.value === "object" && res.value?.triggered === true) return true;
  return false;
}

function disambiguateConditionNames(conditions) {
  const counts = conditions.reduce((acc, c) => {
    const key = c.name || "Condition";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return conditions.map((c) => {
    if ((counts[c.name] || 0) <= 1) return c;

    let suffix = "Condition";
    if (c.nodeType === "comparator") suffix = "Comparator";
    else if (c.nodeType === "logic_gate") suffix = "Logic Gate";
    else if (c.nodeType === "signal") suffix = "Signal";

    return { ...c, name: `${c.name} [${suffix}]` };
  });
}

//--> ONE predicate for "this condition is true right now": a stale/undersized input (E5a
//    invalid_reason) is never true, for the count chip, the side's Ready status, the trigger's
//    parentReady AND the lit spine alike (Codex review F6 — they must never disagree).
function isLiveTrue(res) {
  return !!res && getNodeTruth(res) && !res.invalid_reason;
}
function condIsLiveTrue(c) {
  return c.hasData && c.isMet && !c.staleReason;
}

//* 50-21 R10-T5/T6 — `nameInfo` = { name, autoText, notes } from `buildNameInfo` (below), computed ONCE per
//  graph/record change (not per WS tick). The ONE display-name resolver feeds the row's primary text; the
//  CURRENT auto text and the author's notes ride in the row DETAIL while a record custom_name is set.
//  Only the LABEL of the record reaches this surface — never its styling members.
function buildNameInfo(node, nodes, edges, entry, record) {
  return { name: resolveDisplayName(node, nodes, edges, entry, record), ...resolveNameDetail(node, nodes, edges, entry, record) };
}

function decorateCond(node, r, nameInfo) {
  const res = r[node.id];
  //* D25 — ONE call to the shared value-substitution function: `detail` (click-to-reveal, comparator /
  //  logic_gate strings unchanged) and `text` (a time window's bounds, shown in the row).
  //* 50-17 — outcome = this row's own verdict on the same bar (none when absent or withheld: no suffix)
  const cd = buildConditionDetail(node, {
    valueOf: (id) => r[id]?.metadata?.outputs ?? r[id]?.value,
    outcome: res && !res.invalid_reason ? getNodeTruth(res) : null,
  });
  return {
    id: node.id,
    nodeType: node.type,
    name: nameInfo?.name || node.type || "Condition",
    autoText: nameInfo?.autoText ?? null,
    notes: nameInfo?.notes ?? null,
    detail: cd?.detail ?? null,
    conditionText: cd?.text ?? null,
    hasData: !!res,
    isMet: getNodeTruth(res),
    //! E5a — sourced from the SAME `res` object above. Optional on the wire (omitted by
    //  to_dict() when None), so a pre-fix worker payload leaves these `null` and rendering
    //  falls through to today's met/miss/wait behaviour unchanged.
    staleReason: res?.invalid_reason ?? null,
    ageMs: res?.age_ms ?? null,
  };
}
function decorateTrig(node, r, engineDecision) {
  const res = r[node.id];
  //! Board #73 — engine's real per-bar decision, matched by node_id. `engine_decision`
  //  is a single object on the wire (the most recently triggered signal node this bar),
  //  omitted entirely when no signal node fired (E5a convention) — so this stays null on
  //  a pre-fix worker payload or a bar with nothing to report, preserving today's
  //  met/miss/wait behaviour unchanged (same absent-field back-compat convention as
  //  staleReason/ageMs above).
  const matchesEngine = !!(engineDecision && engineDecision.node_id === node.id);
  return {
    id: node.id,
    node,
    action: node.data?.action,
    label: node.data?.label || node.data?.action?.toUpperCase() || "TRIGGER",
    hasData: !!res,
    isMet: getNodeTruth(res),
    staleReason: res?.invalid_reason ?? null,
    ageMs: res?.age_ms ?? null,
    engineVerdict: matchesEngine ? engineDecision.verdict ?? null : null,
    engineReason: matchesEngine ? engineDecision.reason ?? null : null,
    engineSinceMs: matchesEngine ? engineDecision.since_ms ?? null : null,
    //! R8.6-R8.10 — the OMS-outcome axis (below) is keyed off the FIRED signal's
    //  signal_id, distinct from node_id above. `engine_decision` does not carry
    //  signal_id on today's wire (instance_manager.py's `_engine_decision` dict
    //  omits it) — this stays `null` until a backend change adds it, at which
    //  point the omsOutcomes lookup below activates with no FE change needed.
    //  Until then this deliberately renders "No outcome recorded" (honest
    //  absence, R8.8) rather than a wrong or guessed match.
    signalId: matchesEngine ? engineDecision.signal_id ?? null : null,
  };
}

/*>>> NUMBER FORMATTING <<<*/
const INR = (v) => (v == null || Number.isNaN(+v) ? "—" : Number(v).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }));
const INR_ABS = (v) => (v == null || Number.isNaN(+v) ? "—" : Number(Math.abs(v)).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }));
const SIGNED_INR = (v) => {
  if (v == null || Number.isNaN(+v)) return "—";
  const n = Number(v);
  if (n > 0) return `+${INR_ABS(n)}`;
  if (n < 0) return `−${INR_ABS(n)}`;
  return INR_ABS(0);
};
const SIGNED_PCT = (v) => {
  if (v == null || Number.isNaN(+v)) return "—";
  const n = Number(v);
  return (n > 0 ? "+" : n < 0 ? "−" : "") + Math.abs(n).toFixed(2) + "%";
};

/*>>> PRIMITIVES <<<*/
function Chip({ children, className = "", dot }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-[3px] rounded-full ${className}`}>
      {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
      {children}
    </span>
  );
}

function Stat({ label, value, valueClass = "text-fg-1" }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium text-fg-2">{label}</span>
      <span className={`text-[13px] font-mono tabular-nums font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}

function toFinite(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function getDistanceModel({ side, slPrice, tpPrice, mark, entryPrice }) {
  const sl = toFinite(slPrice);
  const tp = toFinite(tpPrice);
  const live = toFinite(mark);
  const entry = toFinite(entryPrice);
  if (sl == null || tp == null || live == null || sl === tp) return null;

  //--> Compute [0..1] position. 0 = at SL, 1 = at TP
  let fromStopPct;
  if (side === "long") {
    //* LONG: SL below, TP above. Price going UP from SL → TP is good.
    fromStopPct = (live - sl) / (tp - sl);
  } else {
    //* SHORT: SL above, TP below. Price going DOWN from SL → TP is good.
    fromStopPct = (sl - live) / (sl - tp);
  }

  const clamped = Math.max(0, Math.min(1, fromStopPct));
  const toStopSigned = side === "long" ? live - sl : sl - live;
  const toTargetSigned = side === "long" ? tp - live : live - tp;

  //--> Center-origin progress math: entry is neutral (50%), favorable move pushes right (green),
  //*  adverse move pushes left (red). Side normalization keeps semantics identical for long/short.
  const axisEntry = entry ?? (sl + tp) / 2;
  const axisLive = side === "long" ? live - axisEntry : axisEntry - live;
  const axisStop = side === "long" ? sl - axisEntry : axisEntry - sl;
  const axisTarget = side === "long" ? tp - axisEntry : axisEntry - tp;
  const stopRange = Math.max(1e-9, Math.abs(axisStop));
  const targetRange = Math.max(1e-9, Math.abs(axisTarget));

  const favorableRatio = axisLive >= 0 ? Math.max(0, Math.min(1, axisLive / targetRange)) : 0;
  const adverseRatio = axisLive < 0 ? Math.max(0, Math.min(1, (-axisLive) / stopRange)) : 0;
  const markerPos = axisLive >= 0 ? 50 + favorableRatio * 50 : 50 - adverseRatio * 50;
  const isFavorable = axisLive >= 0;

  return {
    sl,
    tp,
    live,
    clamped,
    fromStopPct,
    toStopSigned,
    toTargetSigned,
    markerPos,
    favorableRatio,
    adverseRatio,
    isFavorable,
  };
}

/*>>> DISTANCE VIEW — one inner well: stop | target, center-origin bar <<<*/
function StopTargetSplitView({ side, slPrice, tpPrice, mark, entryPrice }) {
  const model = getDistanceModel({ side, slPrice, tpPrice, mark, entryPrice });
  if (!model) return null;

  const { sl, tp, live, fromStopPct, toStopSigned, toTargetSigned, markerPos, favorableRatio, adverseRatio, isFavorable } = model;
  const beyondRange = fromStopPct < 0 || fromStopPct > 1;
  const pathHint = side === "long"
    ? "Heading: stop -> higher prices -> target"
    : "Heading: stop -> lower prices -> target";
  const hintId = `distance-direction-${side}`;
  const activeWidth = `${(isFavorable ? favorableRatio : adverseRatio) * 50}%`;
  const activeLeft = isFavorable ? "50%" : `${50 - adverseRatio * 50}%`;
  const entry = toFinite(entryPrice);

  return (
    <div className="rounded-[10px] bg-bg-3 px-3.5 py-3">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[11px] font-medium text-fg-2">Stop price</div>
          <div className="font-mono tabular-nums text-[12px] font-semibold text-loss">{INR(sl)}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-medium text-fg-2">Target price</div>
          <div className="font-mono tabular-nums text-[12px] font-semibold text-profit">{INR(tp)}</div>
        </div>
      </div>

      <div
        className="relative h-1.5 my-3 rounded-full bg-bg-2"
        role="progressbar"
        aria-label={`Center-origin distance between stop and target for ${side} position`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(markerPos)}
        aria-valuetext={`${Math.round(markerPos)} on center-origin scale, current ${INR(live)}, stop ${INR(sl)}, target ${INR(tp)}`}
        aria-describedby={hintId}
      >
        <div className="absolute inset-y-0 left-0 w-1/2 rounded-l-full bg-loss/25" />
        <div className="absolute inset-y-0 right-0 w-1/2 rounded-r-full bg-profit/25" />
        <div
          className={`absolute inset-y-0 ${isFavorable ? "bg-profit" : "bg-loss"} motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out motion-reduce:transition-none`}
          style={{ left: activeLeft, width: activeWidth }}
        />
        <div className="absolute -top-1 left-1/2 h-3.5 w-px bg-fg-2" />
        <div
          className="absolute top-1/2 h-4 w-4 -translate-y-1/2 box-border rounded-full border-[3px] border-bg-3 bg-fg-1 motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out motion-reduce:transition-none"
          style={{ left: `calc(${markerPos}% - 8px)` }}
          data-testid="distance-center-marker"
        />
      </div>

      <div className="flex items-center justify-between text-[11px] text-fg-2 tabular-nums">
        <span>{toStopSigned >= 0 ? `${INR_ABS(toStopSigned)} to stop` : "Stop breached"}</span>
        <span>Entry {INR(entry)}</span>
        <span>{toTargetSigned >= 0 ? `${INR_ABS(toTargetSigned)} to target` : "Past target"}</span>
      </div>
      {beyondRange && (
        <div className="mt-1 text-center text-[11px] text-fg-2">{fromStopPct < 0 ? "Past stop" : "Past target"}</div>
      )}

      <div className="sr-only">
        <div>Current price</div>
        <div>{INR(live)}</div>
        <span id={hintId}>{pathHint}</span>
      </div>
      <div className="sr-only" data-testid="distance-bar-positive-range">center to right is favorable (green)</div>
      <div className="sr-only" data-testid="distance-bar-negative-range">center to left is adverse (red)</div>
    </div>
  );
}

/*>>> SUMMARY BLOCK — header row + P&L / status (no box: the parent card owns the surface) <<<*/
function SummaryCard({ side, statusText, statusClass, inPosition, position, liveLtp, configured }) {
  const tokens = SIDE[side];

  const entry = Number(position?.avg_entry_price ?? 0);
  const qty = Number(position?.qty ?? 0);
  const multiplier = Number(position?.contract_multiplier ?? 1);
  const mark = Number(liveLtp ?? 0);
  let pnl = null, pnlPct = null;
  if (inPosition && entry > 0 && mark > 0 && qty > 0) {
    const dir = side === "long" ? 1 : -1;
    pnl = (mark - entry) * qty * multiplier * dir;
    pnlPct = ((mark / entry - 1) * 100) * dir;
  }
  const pnlInk = pnl == null ? "text-fg-3" : pnl > 0 ? "text-profit" : pnl < 0 ? "text-loss" : "text-fg-3";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 flex-shrink-0 rounded-full ${!configured ? "bg-bg-3" : tokens.dot}`} />
        <span className="text-[15px] font-semibold text-fg-1">{tokens.label}</span>
        <Chip className={`ml-auto ${statusClass}`} dot={inPosition ? tokens.dot : null}>
          {statusText}
        </Chip>
      </div>

      {!configured ? (
        <div className="text-[11.5px] italic text-fg-3">
          Not configured on this strategy version — deploy a newer version to enable {tokens.label.toLowerCase()} entries.
        </div>
      ) : inPosition ? (
        <div>
          <div className="text-[11px] font-medium text-fg-2">Unrealized P&amp;L</div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className={`text-[28px] font-bold tracking-tight tabular-nums leading-tight ${pnlInk}`}>
              {SIGNED_INR(pnl)}
            </span>
            <span className={`text-[13px] font-semibold tabular-nums ${pnlInk}`}>{SIGNED_PCT(pnlPct)}</span>
          </div>
          <div className="mt-2.5 flex flex-wrap items-end gap-x-6 gap-y-2">
            <Stat label="Entry" value={INR(entry)} />
            {/* order-quantity-canonicalization: `qty` is UNITS everywhere now (never lots) —
                the pre-existing `${qty} × ${lotSize}` display assumed qty was LOTS (lotSize was
                the deleted local var this literally could not compile against; ReferenceError on
                every in-position render). Showing qty bare is correct post-canonicalization: it
                is already the full, authoritative unit count — mirrors TradeCard.jsx/
                OrderDetailDrawer.jsx, which display qty and lot_size as SEPARATE fields, never
                combined via multiplication (R5.5, D9). */}
            <Stat label="Qty" value={`${qty}`} />
            {/* DX review 2026-10-22: "LTP" is the app's (and every Indian broker's) word; "Mark"
                read as settlement jargon. Price change / % / arrow and the sparkline live ONLY in
                the page-header pill (StrategyDeepDive), never duplicated per side. */}
            <Stat label="LTP" value={INR(mark)} />
          </div>
        </div>
      ) : (
        <div>
          <div className="text-[11px] font-medium text-fg-2">Status</div>
          <div className="mt-0.5 text-[20px] font-semibold text-fg-1">Awaiting entry</div>
        </div>
      )}
    </div>
  );
}

/*>>> INVALID-REASON LABELS — the wire's `invalid_reason` rendered as a specific, plain-language chip <<<*/
//  Deepak 2026-10-22: "NOT WARM" used to stand in for EVERY non-stale reason, so a model
//  timeout and a missing hourly context both read as "still warming up". One authority for
//  the vocabulary; an unknown reason is humanized (`_` -> space, upper-case), never dropped
//  (feedback_silent_failure_antipattern). `warmup` is the only reason that IS warm-up.
const INVALID_REASON_LABELS = {
  stale: null,                       // rendered with its age: "STALE 12m"
  undersized: "NOT WARM",            // indicator buffer shorter than its lookback (warm-up)
  no_data: "NO DATA",
  direction_unknown: "DIRECTION UNKNOWN",
  unresolved_input: "INPUT UNAVAILABLE",
  timeout: "MODEL TIMEOUT",
  context_unavailable: "NO MODEL CONTEXT",
  v2_serving_unavailable: "MODEL UNAVAILABLE",
  inference_failed: "MODEL FAILED",
  degraded: "MODEL DEGRADED",
  exception: "MODEL ERROR",
  breaker_open: "MODEL PAUSED",
};
function invalidReasonLabel(reason, ageMs) {
  if (reason === "stale") return `STALE ${formatAge(ageMs)}`;
  const known = INVALID_REASON_LABELS[reason];
  if (known) return known;
  return String(reason || "unavailable").replace(/_/g, " ").toUpperCase();
}
//--> True warm-up vs a real problem: everything that is not plain warm-up is a problem the
//    operator should see at the top of the tree, not only on the row.
function isWarmupReason(reason) {
  return reason === "undersized";
}

/*>>> PROBLEM LINE — the tree's invalid inputs, stated once above the rows <<<*/
//  Warm-up alone reads "n inputs warming up" (neutral); any other reason reads as a warning
//  with the distinct reasons spelled out, so an operator sees "MODEL TIMEOUT" without
//  scanning rows.
function ProblemLine({ conditions }) {
  const invalid = (conditions || []).filter((c) => c.hasData && c.staleReason);
  if (invalid.length === 0) return null;
  const reasons = Array.from(new Set(invalid.map((c) => c.staleReason)));
  const onlyWarmup = reasons.every(isWarmupReason);
  const labels = reasons.map((r) => invalidReasonLabel(r, null)).join(" · ");
  const text = onlyWarmup
    ? `${invalid.length} ${invalid.length === 1 ? "input" : "inputs"} warming up`
    : `${invalid.length} ${invalid.length === 1 ? "input" : "inputs"} unavailable · ${labels}`;
  return (
    <div
      role={onlyWarmup ? undefined : "status"}
      data-testid="tree-problem-line"
      data-severity={onlyWarmup ? "info" : "warning"}
      className={`mb-1.5 flex items-center gap-1.5 text-[11px] font-medium ${onlyWarmup ? "text-fg-2" : ""}`}
      style={onlyWarmup ? undefined : { color: "color-mix(in srgb, var(--warning) 55%, var(--fg-1))" }}
      title={invalid.map((c) => `${c.name}: ${c.staleReason}`).join("\n")}
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${onlyWarmup ? "bg-fg-3" : "bg-warning"}`} />
      {text}
    </div>
  );
}

/*>>> TRUE COUNT — "n / N true" beside a tree header; the ONE place progress is stated <<<*/
//  (the tree never pretends to show progress on this surface). Stale rows do not count as true.
function TrueCount({ conditions }) {
  if (!conditions?.length) return null;
  const met = conditions.filter(condIsLiveTrue).length;
  return (
    <span
      className="ml-auto flex-shrink-0 rounded-full bg-bg-3 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-fg-2"
      aria-label={`${met} of ${conditions.length} conditions true`}
    >
      {met} / {conditions.length} true
    </span>
  );
}

/*>>> OMS-OUTCOME CHIP (R8.6-R8.10) — a DIFFERENT axis from triggerState/engineVerdict above:
   what OMS did with the signal, not what the engine decided pre-OMS. Never folded into
   triggerState (lineage row L-6/D31 — a rejected and a filled signal must never render
   identically). Three literal states, never a blank chip (R8.8 honest-absence). <<<*/
function OutcomeChip({ outcome }) {
  const kind = outcome?.kind ?? "none";
  let text, cls;
  if (kind === "placed") {
    text = "Placed";
    cls = "text-profit bg-profit/[0.12]";
  } else if (kind === "rejected") {
    //* R8.9 — reuse rejectReasonLabel() verbatim, never a local humanizer.
    text = `Rejected — ${rejectReasonLabel(fullRejectReason(outcome)) ?? outcome.reject_reason ?? "unknown"}`;
    cls = "text-loss bg-loss/[0.12]";
  } else {
    text = "No outcome recorded";
    cls = "text-fg-2 bg-bg-3";
  }
  //* R8.7/R8.10 — same 3-state mode badge logic as OrdersTable's ModeBadge, extended not
  //  duplicated. execution_mode is ALREADY server-derived — never re-derive it here.
  const mode = (outcome?.execution_mode ?? "").toUpperCase();
  return (
    <div className="flex items-center gap-2 mt-0.5 mb-1 pl-[22px]" data-testid="oms-outcome-chip">
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>
        {text}
      </span>
      {mode === "LIVE" && <ModeBadge mode="LIVE" />}
      {mode === "PAPER" && <ModeBadge mode="PAPER" />}
      {mode === "UNATTRIBUTED" && <ModeBadge mode="UNATTRIBUTED" />}
    </div>
  );
}

/*>>> MINI-TREE — left-rail layout, conditions branch off spine ending at trigger <<<*/
function MiniTree({ header, conditions, trigger, sideTokens, accentTokens, omsOutcomes, tree, live, labelOf }) {
  const triggerFired = trigger.hasData && trigger.isMet;
  const isExitTrigger = EXIT_ACTIONS.has(trigger.action);
  const triggerReady = !triggerFired && !isExitTrigger && trigger.parentReady === true;
  //! Board #73 — the engine's real verdict takes priority over the pre-gate `isMet`
  //  truth, mirroring the staleReason-over-isMet precedent for conditions above (E5a,
  //  lineage-D1). A node whose graph truth reads TRUE but whose engine_decision says
  //  "suppressed" must never render as Fired — that IS Symptom #1, the defect this
  //  fix exists to close.
  const isDeferred = trigger.engineVerdict === "suppressed";
  const triggerState = isDeferred ? "deferred" : triggerFired ? "fired" : triggerReady ? "ready" : "waiting";
  //! F4 (QA iter-4, CRITICAL) — `trigger.engineReason` was decorated onto the trigger object
  //  (decorateTrig, :231) but never READ here, so every suppressed verdict rendered the SAME
  //  generic "DEFERRED · ENTRY PENDING" chip no matter WHY the engine refused the entry. That
  //  makes a worker fail-closed on OMS_BOOT_HYDRATE_PENDING (refusing every entry because OMS
  //  position truth is unknown at boot — #76-B) visually indistinguishable on this panel from a
  //  worker quietly waiting on same_side_open / not_rearmed / naked_exit / a plain
  //  entry_pending. Reason casing is NOT uniform on the wire (OMS_BOOT_HYDRATE_PENDING is
  //  UPPER_CASE; its four siblings are lower_snake_case) — humanize with a plain `_` -> space
  //  swap only, no case switch, no allow-list/lookup map, so an unrecognised future reason
  //  still renders legibly instead of silently disappearing behind the old placeholder
  //  (feedback_silent_failure_antipattern). The status span already carries the `uppercase`
  //  CSS class on the trigger chip, so visual case is normalized for free either way.
  const reasonLabel = trigger.engineReason ? String(trigger.engineReason).replace(/_/g, " ") : null;
  //--> DX review 2026-10-22: the chip stays short ("DEFERRED · 12m") so a long reason such as
  //    OMS_BOOT_HYDRATE_PENDING cannot crush the row name at laptop width; the humanized reason
  //    (same `_` -> space rule, still never dropped) renders on the row's detail line instead.
  const triggerStatus = isDeferred
    ? `DEFERRED · ${formatAge(trigger.engineSinceMs)}`
    : triggerFired ? "Fired" : triggerReady ? "Ready" : "Waiting";
  const triggerDetail = isDeferred ? `Suppressed: ${reasonLabel || "entry pending"}` : null;

  //--> accentTokens provides rail color + nodeMet + triggerReady + dot/ink for destination
  const treeAccent = { dot: sideTokens.rail, triggerReady: accentTokens.triggerReady };
  //--> The spine lights only when the signal flows to the trigger (Ready/Fired), amber when the
  //    engine deferred it. Any-condition-true never lights it.
  const spineLit = isDeferred ? "warning" : (triggerFired || triggerReady) ? "accent" : null;

  //--> D25 — a self-contained trigger (sl / tp / time_exit) states its own parameters (+ delivered level / LTP);
  //    its truth does not come from input edges, so a stray comparator edge never renders as a gating child
  //    (F-d25-4). TrueCount / ProblemLine read the same tree the rows render (none when self-contained, F-1).
  const triggerCond = buildConditionDetail(trigger.node, live || {});
  const selfContained = !!triggerCond?.text;
  const headerConditions = selfContained ? [] : conditions;
  const emptyTree = useMemo(() => (tree ? { ...tree, children: [] } : tree), [tree]);

  //--> rowOf: the decorated condition for each tree node (value / invalid fact / chip text are this surface's own)
  const conditionsById = useMemo(() => new Map(conditions.map((c) => [c.id, c])), [conditions]);
  //--> fallback row name for a tree node with no decorated condition: the node's own label, never the raw id
  const treeLabels = useMemo(() => {
    const m = new Map();
    const walk = (t) => (t?.children || []).forEach((ch) => {
      if (ch?.node && !m.has(ch.node.id)) m.set(ch.node.id, labelOf?.(ch.node) || ch.node.type || ch.node.id);
      walk(ch);
    });
    walk(tree);
    return m;
  }, [tree, labelOf]);
  const rowOf = useCallback((id) => {
    const c = conditionsById.get(id);
    if (!c) return { name: treeLabels.get(id) || id, value: null, chipText: "—", railState: "wait" };
    //! E5a/lineage-D1 — `c.staleReason` (present only when the evaluator withheld an input) takes priority
    //  over isMet so a stale/undersized node NEVER collapses into the same "False" chip a legitimately-unmet
    //  comparator renders. Absent on a pre-fix worker payload (decorateCond defaults it to `null`).
    const state = !c.hasData ? "wait" : c.staleReason ? "stale" : c.isMet ? "met" : "miss";
    const chipText =
      state === "met" ? "True" :
      state === "miss" ? "False" :
      state === "stale" ? invalidReasonLabel(c.staleReason, c.ageMs) :
      "—";
    return {
      name: c.name,
      detail: c.detail,
      autoText: c.autoText,
      notes: c.notes,
      conditionText: c.conditionText,
      value: c.hasData ? c.isMet : null,
      invalidReason: c.staleReason,
      chipText,
      railState: state,
      //--> raw wire reason on hover (the chip shows the plain-language label)
      title: state === "stale" ? `Reason: ${c.staleReason}` : null,
    };
  }, [conditionsById, treeLabels]);

  //--> Hover affordance: the RAW, un-humanized reason string verbatim (untouched case), for
  //*  anyone who wants the exact wire value rather than the humanized chip text above.
  const triggerTitle = isDeferred && trigger.engineReason ? `Suppression reason: ${trigger.engineReason}` : null;

  //--> R8.6 render hook — adjacent, independent of triggerState above (see OutcomeChip doc).
  const outcome = trigger.signalId ? omsOutcomes?.get(trigger.signalId) : undefined;

  return (
    <div>
      {header && (
        <div className="mb-0.5 flex items-center gap-2 text-[12px] font-semibold text-fg-1">
          <span className={`h-2 w-2 flex-shrink-0 rounded-full ${accentTokens.dot}`} />
          {header}
          <TrueCount conditions={headerConditions} />
        </div>
      )}
      {header && <ProblemLine conditions={headerConditions} />}
      {/* R8.6 — OutcomeChip sits directly under the trigger row (triggerRow.aside), independent axis,
          never a blank chip */}
      <ConditionRailTree
        tree={selfContained ? emptyTree : tree}
        rowOf={rowOf}
        accent={treeAccent}
        flow={spineLit}
        showCount={false}
        triggerRow={{
          name: trigger.label,
          detail: triggerDetail,
          title: triggerTitle,
          state: triggerState,
          statusLabel: triggerStatus,
          conditionText: triggerCond?.text,
          conditionDetail: triggerCond?.detail,
          aside: <OutcomeChip outcome={outcome} />,
        }}
      />
    </div>
  );
}

/*>>> SIDE COLUMN <<<*/
function SideColumn({ side, configured, position, liveLtp, entryConditions, entryTrigger, entryTree, exitSections, slPrice, tpPrice, omsOutcomes, labelOf }) {
  const tokens = SIDE[side];
  const upperSide = side.toUpperCase();
  const inThisPosition = position && position.side === upperSide;
  const inOtherPosition = position && position.side !== "FLAT" && position.side !== upperSide;
  const hasDistanceInputs =
    toFinite(slPrice) != null &&
    toFinite(tpPrice) != null &&
    toFinite(liveLtp) != null;

  //* D25 — live values the shared detail function substitutes. The SL/TP pair (slPrice / tpPrice) is per
  //  POSITION and trade_context carries no node_id, so a level is attributed to a row only when this side has
  //  exactly ONE self-contained stop-loss (take-profit) section; with two the rows show parameters only (F-d25-5).
  const selfContainedOf = (action) =>
    exitSections.filter((x) => x.trigger.action === action && !!buildConditionDetail(x.trigger.node)?.text).length;
  const live = {
    ltp: liveLtp,
    stop: selfContainedOf("sl") === 1 ? slPrice : null,
    target: selfContainedOf("tp") === 1 ? tpPrice : null,
  };

  const NEUTRAL_PILL = "bg-bg-3 text-fg-2";
  let statusText, statusClass;
  if (!configured) {
    statusText = "Not configured";
    statusClass = NEUTRAL_PILL;
  } else if (inThisPosition) {
    statusText = "In position";
    statusClass = tokens.pillTint;
  } else if (inOtherPosition) {
    statusText = "Standby";
    statusClass = NEUTRAL_PILL;
  } else if (entryTrigger && entryConditions.length > 0 && entryConditions.every(condIsLiveTrue)) {
    statusText = "Ready";
    statusClass = tokens.pillTint;
  } else {
    statusText = "Awaiting entry";
    statusClass = NEUTRAL_PILL;
  }

  return (
    <div className="flex flex-col gap-4 p-5">
      <SummaryCard
        side={side}
        statusText={statusText}
        statusClass={statusClass}
        inPosition={inThisPosition}
        position={position}
        liveLtp={liveLtp}
        configured={configured}
      />

      {/*>>> STOP/TARGET COMPARE — one inner well <<<*/}
      {inThisPosition && hasDistanceInputs && (
        <StopTargetSplitView
          side={side}
          slPrice={slPrice}
          tpPrice={tpPrice}
          mark={Number(liveLtp)}
          entryPrice={position?.avg_entry_price}
        />
      )}

      {configured && (
        inThisPosition ? (
          <div>
            <div className="mb-2 flex items-baseline gap-2">
              <b className="text-[12px] font-semibold text-fg-1">Exit monitor</b>
              <span className="text-[11px] text-fg-2">
                {exitSections.length} {exitSections.length === 1 ? "trigger" : "triggers"} armed
              </span>
            </div>
            {exitSections.length === 0 ? (
              <div className="text-[11.5px] italic text-fg-3">No exit triggers configured.</div>
            ) : (
              <div className="flex flex-col gap-3.5">
                {exitSections.map((s) => {
                  const accent = EXIT_ACCENT[s.trigger.action] || EXIT_ACCENT.sl;
                  return (
                    <MiniTree
                      key={s.trigger.id}
                      header={s.trigger.label}
                      sideTokens={tokens}
                      accentTokens={{ nodeMet: tokens.nodeMet, triggerReady: accent.triggerReady, dot: accent.dot, ink: accent.ink }}
                      conditions={s.conditions}
                      trigger={s.trigger}
                      tree={s.tree}
                      live={live}
                      omsOutcomes={omsOutcomes}
                      labelOf={labelOf}
                    />
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <b className="text-[12px] font-semibold text-fg-1">Entry signal</b>
              <span className="text-[11px] text-fg-2">{tokens.label} entry</span>
              <TrueCount conditions={entryConditions} />
            </div>
            <ProblemLine conditions={entryConditions} />
            <MiniTree
              sideTokens={tokens}
              accentTokens={{ nodeMet: tokens.nodeMet, triggerReady: tokens.triggerReady, dot: tokens.rail, ink: tokens.inkSoft }}
              conditions={entryConditions}
              trigger={entryTrigger}
              tree={entryTree}
              live={live}
              omsOutcomes={omsOutcomes}
              labelOf={labelOf}
            />
          </div>
        )
      )}
    </div>
  );
}

const EMPTY_RECORD = Object.freeze({});

/*>>> MAIN PANEL <<<*/
const SignalEvaluationPanel = ({
  position = null,
  liveLtp = null,
  strategyConfig = { nodes: [], edges: [] },
  nodeResults = {},
  //! Board #73 — engine's real post-gate verdict for the most recently
  //!  triggered signal node, forwarded straight through from MonitoringTab
  //!  (same useLiveExecution() call as nodeResults, never a separate one).
  engineDecision = null,
  orders = [],
  liveLevels = null,
  //! R8.6-R8.10 — Map<signal_id, {kind, reject_reason, sub_reason, execution_mode}>,
  //  built by utils/omsOutcomes.js's deriveOmsOutcomes helper. Empty map -> every chip renders
  //  "No outcome recorded" (honest absence), never a crash.
  omsOutcomes = new Map(),
  //* 50-21 R10-T5 — node id -> display-record entry ({custom_name, notes, ...}). MonitoringTab loads it ONCE
  //  per mount (read-only, zero PUTs) and hands it down; this panel stays presentational (no fetch).
  visualOverrides: displayRecord = EMPTY_RECORD,
}) => {
  //--> the ONE resolver, bound to this strategy's graph + record (fallback names for undecorated tree nodes)
  const labelOf = useCallback(
    (node) => resolveDisplayName(node, strategyConfig?.nodes || [], strategyConfig?.edges || [], displayRecord?.[node?.id], displayRecord),
    [strategyConfig?.nodes, strategyConfig?.edges, displayRecord],
  );
  //--> Derive live SL/TP levels for the active position (H4: linear scan, no sort/allocation).
  //*  OrderEvent carries `stop_loss` / `take_profit` on entry orders. Find the newest FILLED entry
  //*  for the active strategy_instance and read them directly.
  const { slPrice, tpPrice } = useMemo(() => {
    const num = (v) => (v == null || v === "" ? null : Number(v));

    //--> #10472 (R9) — locate the newest FILLED entry order for this position (if any) so its
    //*   bracket LEG ORDER ROWS (parent_order_id === newestEntry.id) can be read first — the
    //*   protective order rows are the authority for what actually protects the position, same
    //*   rows the Orders page / TradeCard leg table render.
    let newestEntry = null;
    if (position && position.side !== "FLAT" && orders?.length) {
      const sid = position.strategy_instance_id;
      let newestEpoch = -1;
      for (const o of orders) {
        if (o.strategy_instance_id !== sid) continue;
        const isEntry = o.leg_type === "entry" || (!o.leg_type && !o.parent_order_id && !o.exit_reason);
        if (!isEntry) continue;
        if (o.status !== "FILLED" && o.filled_price == null) continue;
        const epoch = o._epoch || 0;
        if (epoch > newestEpoch) {
          newestEpoch = epoch;
          newestEntry = o;
        }
      }
    }

    const bracketLegs = newestEntry ? orders.filter((o) => o.parent_order_id === newestEntry.id) : [];
    //--> Next: live trade_context levels when both endpoints are available.
    const liveSl = num(liveLevels?.stopLoss);
    const liveTp = num(liveLevels?.takeProfit);
    const liveOk = Number.isFinite(liveSl) && Number.isFinite(liveTp) && liveSl !== liveTp;
    //--> ONE pair-level gating rule shared with TradeCard (pickLevelPair): bracket leg rows, then
    //*   liveLevels, then the entry order's construction-time copy — never mixed across sources.
    const pair = pickLevelPair(
      resolveCurrentBracketLevels(bracketLegs),
      liveOk ? { sl: liveSl, tp: liveTp } : null,
      newestEntry ? { sl: num(newestEntry.stop_loss), tp: num(newestEntry.take_profit) } : null,
    );
    return { slPrice: pair.sl, tpPrice: pair.tp };
  }, [position, orders, liveLevels]);

  //--> H3: Split model derivation. GRAPH SHAPE is precomputed from strategyConfig (stable) —
  //*  includes trigger nodes, their upstream condition node lists, and an incoming-edge count map
  //*  used for sorting. Cheap per-node result lookup happens in the second memo below.
  const graphShape = useMemo(() => {
    const nodes = strategyConfig?.nodes || [];
    const edges = strategyConfig?.edges || [];
    const cfg = { nodes, edges };
    //--> Precompute incoming-edge count per node id — drops the O(E) filter-in-sort-comparator
    const incomingCount = new Map();
    for (const e of edges) {
      incomingCount.set(e.target, (incomingCount.get(e.target) || 0) + 1);
    }
    const build = (s) => {
      const entryNode = findEntryTrigger(nodes, s);
      const configured = !!entryNode;
      //* 50-17 — the ONE walk: each trigger's tree is built here (stable graph memo) and handed down.
      const entryTree = entryNode ? collectTriggerTree(entryNode.id, nodes, edges) : null;
      const entryConditionNodes = entryNode ? collectUpstream(cfg, entryTree) : [];
      const entryParentIds = entryNode ? getTriggerParentIds(cfg, entryNode.id) : [];
      entryConditionNodes.sort((a, b) => (incomingCount.get(a.id) || 0) - (incomingCount.get(b.id) || 0));
      const exitTriggerNodes = findExitTriggers(nodes, s).map((t) => {
        const tree = collectTriggerTree(t.id, nodes, edges);
        const cs = collectUpstream(cfg, tree);
        cs.sort((a, b) => (incomingCount.get(a.id) || 0) - (incomingCount.get(b.id) || 0));
        return { triggerNode: t, tree, conditionNodes: cs, parentIds: getTriggerParentIds(cfg, t.id) };
      });
      return { configured, entryNode, entryTree, entryConditionNodes, entryParentIds, exitTriggerNodes };
    };
    return { long: build("long"), short: build("short") };
  }, [strategyConfig?.nodes, strategyConfig?.edges]);

  //--> 50-21: names resolved once per graph / record change, so a WS tick never re-walks the graph for labels
  const nameInfoById = useMemo(() => {
    const nodes = strategyConfig?.nodes || [];
    const edges = strategyConfig?.edges || [];
    return new Map(nodes.map((n) => [n.id, buildNameInfo(n, nodes, edges, displayRecord?.[n.id], displayRecord)]));
  }, [strategyConfig?.nodes, strategyConfig?.edges, displayRecord]);

  //--> H3: Decorate with live results — cheap lookups, runs on every nodeResults tick.
  const models = useMemo(() => {
    const build = (s) => {
      const shape = graphShape[s];
      const entryConditions = disambiguateConditionNames(shape.entryConditionNodes.map((n) => decorateCond(n, nodeResults, nameInfoById.get(n.id))));
      const entryParentReady =
        shape.entryParentIds.length > 0 &&
        shape.entryParentIds.every((id) => isLiveTrue(nodeResults[id]));
      const entryTrigger = shape.entryNode
        ? { ...decorateTrig(shape.entryNode, nodeResults, engineDecision), parentReady: entryParentReady }
        : { id: null, action: null, label: `Enter ${s.charAt(0).toUpperCase() + s.slice(1)}`, hasData: false, isMet: false, parentReady: false };
      const exitSections = shape.exitTriggerNodes.map(({ triggerNode, tree, conditionNodes, parentIds }) => ({
        tree,
        trigger: {
          ...decorateTrig(triggerNode, nodeResults, engineDecision),
          parentReady: parentIds.length > 0 && parentIds.every((id) => isLiveTrue(nodeResults[id])),
        },
        conditions: disambiguateConditionNames(conditionNodes.map((n) => decorateCond(n, nodeResults, nameInfoById.get(n.id)))),
      }));
      return { configured: shape.configured, entryConditions, entryTrigger, entryTree: shape.entryTree, exitSections };
    };
    return { long: build("long"), short: build("short") };
  }, [graphShape, nodeResults, engineDecision, nameInfoById]);

  const isLoading = !strategyConfig?.nodes || strategyConfig.nodes.length === 0;
  if (isLoading) {
    return <div className="text-sm italic text-fg-3 text-center py-16">Loading strategy configuration…</div>;
  }

  //* R8.6-R8.10 — the full per-signal outcome list lives in SignalActivity (Monitoring tab
  //  activity card / section); this panel keeps only the per-trigger OutcomeChip in each MiniTree.
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 rounded-xl bg-bg-2 shadow-[var(--shadow-sm)] divide-y divide-divider md:divide-y-0 md:divide-x">
      <SideColumn
        side="long"
        configured={models.long.configured}
        position={position}
        liveLtp={liveLtp}
        entryConditions={models.long.entryConditions}
        entryTrigger={models.long.entryTrigger}
        entryTree={models.long.entryTree}
        exitSections={models.long.exitSections}
        slPrice={slPrice}
        tpPrice={tpPrice}
        omsOutcomes={omsOutcomes}
        labelOf={labelOf}
      />
      <SideColumn
        side="short"
        configured={models.short.configured}
        position={position}
        liveLtp={liveLtp}
        entryConditions={models.short.entryConditions}
        entryTrigger={models.short.entryTrigger}
        entryTree={models.short.entryTree}
        exitSections={models.short.exitSections}
        slPrice={slPrice}
        tpPrice={tpPrice}
        omsOutcomes={omsOutcomes}
        labelOf={labelOf}
      />
      </div>
    </div>
  );
};

export default SignalEvaluationPanel;
